// SelectPlan.jsx
import React, { useContext } from 'react';
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/free-solid-svg-icons';

import PlanList from './planList';
import SessionContext from '../../../context/Session';

const SelectPlan = ({
  featureMsg,
  includeFree,
  nextStep,
  currentSubscription,
  orderInfo = {},
  setOrderInfo,
}) => {
  const { isMobile } = useContext(SessionContext);

  const onSubscribe = async () => nextStep(2, orderInfo);

  const dueDateStr = orderInfo?.dueDate
    ? new Date(orderInfo.dueDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const amountDueStr =
    orderInfo?.amountDue != null
      ? Number(orderInfo.amountDue).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        })
      : null;

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3, marginTop: '-14px' }}>
        <span className="proCrownWrapper">
          <FontAwesomeIcon icon={faCrown} className="proImg" style={{ color: 'gold' }} />
        </span>
        <Typography variant="h6" fontWeight={700}>
          {includeFree ? 'Change your Plan' : 'Upgrade to use this pro feature'}
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        {/* Left column: plan picker + totals */}
        <Grid item xs={12} md={7}>
          <Stack spacing={2}>
            {featureMsg && (
              <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                {featureMsg}
              </Typography>
            )}

            <PlanList orderInfo={orderInfo} setOrderInfo={setOrderInfo} />

            {orderInfo?.includeTrial > 0 && (
              <>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Due on {dueDateStr}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {amountDueStr}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Grid>

        {/* Right column: Pro benefits */}
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            You get more with Pro
          </Typography>

          <List dense disablePadding>
            {[
              'Generate shots for every scene',
              'Generate shots for the entire script in one click',
              'More Fields: Multi-Cam, etc.',
              'Flexible Export Options including CSV',
              'Upload Images and include them in PDF Exports',
            ].map((text) => (
              <ListItem key={text} disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckRoundedIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={text} />
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>

      {/* Footer / Actions */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end' }}>
        <Button
          id="selectPlanNext"
          variant="contained"
          onClick={onSubscribe}
          sx={{ minWidth: 180, borderRadius: 2, px: 4 }}
        >
          Next
        </Button>
      </Box>
    </Paper>
  );
};

export default SelectPlan;
